import React from "react";

const BelievingYouAreNextLevelVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
This lesson contrasts hoping for growth with acting like it is already on the way. Using the umbrella story, it frames four mindsets and highlights believers who behave like the next level before the title. It gives practical ways to practice readiness: learn next level KPIs, seek role specific feedback, ask for access, speak outcome language, feel stakeholder pain, and move in the right circles. It also warns against overreach and burnout while building conviction.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-5 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>There’s an old story…
      <br />
      <p>
      In a small village suffering through months of drought, people gathered
      in the temple to pray for rain. They lit lamps, folded hands, and pleaded
      with the heavens.
      <br />
      But one boy walked in differently.
      <br />
      He carried… an umbrella.
    </p>
    </p>


    <p>
      When the priest asked him why, he said, “Because we’re praying for rain,
      aren’t we?”
      <br />
      The villagers laughed at him. They thought he was being naive.
      <br />
      But he wasn’t.
      <br />
      He was simply the only one who believed his prayer would be answered.
    </p>

    <p>
      The story captures something about us at work too.
      <br />
      We all pray for “rain” — promotions, recognition, growth. We attend
      meetings, complete projects, and wait for the right moment to be noticed.
      But if we’re being honest, most of us show up without an umbrella.
    </p>

    <p>We’re hopeful, but not convinced.</p>

    <p>
      Optimism and conviction are not the same. When you put them together, you
      get four kinds of people:
    </p>

    <ul className="list-disc list-inside space-y-5">
      <li>
        <strong>The Hopeful Optimists:</strong> full of energy, doing the right
        things, but always waiting for someone else to notice.
      </li>
      <li>
        <strong>The Hopeful Pessimists:</strong> they work hard but already have
        one foot out the door, just in case.
      </li>
      <li>
        <strong>The Hopeless:</strong> neither optimistic nor convinced; they’ve
        quietly given up.
      </li>
      <li>
        And then, there are the <strong>Believers:</strong> people who act as if
        the rain is already coming.
      </li>
    </ul>

    <p>They’re the ones who carry the umbrella.</p>

    <p>
      Most promotable people live in this quadrant, the believers. They don’t
      just hope to grow, they behave like they already belong at the next level.
      They speak differently, prepare differently, and move with quiet
      certainty.
      <br />
      So how do you carry your umbrella?
    </p>

    <ol className="list-decimal list-inside space-y-4 pl-4">
      <li>
        <strong>Understand the next-level KPIs.</strong>
        <br />
        Ask plainly: “If I were in that role tomorrow, how would success be
        measured?” That question alone signals intent, and gives you the
        scoreboard to practice against.
      </li>

      <li>
        <strong>Seek the right feedback.</strong>
        <br />
        Don’t ask for code feedback if you’re targeting management. Ask: “How
        do I manage risk? How clear are my escalations?” You want feedback that
        maps to the role you want. Ask like someone already playing the
        position, it changes the answers you get.
      </li>

      <li>
        <strong>Ask for what they have.</strong>
      </li>
    </ol>

    <p>
      Request access to the tools, reports, or privileges the role uses, even
      if you’re turned down. The request itself signals readiness: you’re ready
      to handle more.
    </p>

    <ol start={4} className="list-decimal list-inside space-y-4">
      <li>
        <strong>Speak their language.</strong>
        <br />
        Mirror the next level’s vocabulary, outcomes, revenue, strategic
        tradeoffs. Use their words in updates so your thinking sounds like
        theirs before the title arrives.
      </li>

      <li>
        <strong>Feel their pain.</strong>
        <br />
        If the next role loses sleep over customer churn, learn the customer
        story, own that pain, and bring solutions. Empathy + solutions =
        credibility. Don’t just say “I understand,” <em>prove</em> you do.
        Start showing up in meetings with insights that connect your work to
        that pain.
      </li>

      <li>
        <strong>Move in their circles.</strong>
        <br />
        Connect with the peers and stakeholders the role engages with, inside
        and outside the company. You’ll learn what the job really demands
        beyond the JD.
      </li>
    </ol>

    <p>
      Pause the video here. Open your calendar. Pick the next meeting you’ll
      attend. Ask: <em>Which of the six steps can I practice there?</em> One
      small action, that’s your umbrella for this week.
    </p>

    <p>
      Don’t jump the gun just yet. Slow and steady wins the race remember?
      Before you start acting the next level, here’s a word of caution.
    </p>

    <ul className="list-disc list-inside space-y-1 pl-4">
      <li>
        <strong>Avoid premature overreach.</strong> Don’t take authority you
        haven’t earned, it will definitely backfire.
      </li>
      <li>
        <strong>Stay authentic.</strong> “Fake it till you make it” won’t work,
        grow into it.
      </li>
      <li>
        <strong>Balance confidence with humility.</strong> Be bold, but listen.
      </li>
      <li>
        <strong>Watch for burnout.</strong> Higher-level behavior adds load,
        guard your energy.
      </li>
      <li>
        <strong>Seek constant feedback.</strong> Helps you get back on track if
        you're going the wrong way.
      </li>
      <li>
        <strong>Don’t mistake early wins for mastery.</strong> You can never
        know everything, don’t stop learning
      </li>
      <li>
        <strong>Handle rejection gracefully.</strong> Rejection sharpens
        conviction if you let it.
      </li>
    </ul>

    <p>
      Don’t be “too cool” to carry an umbrella, even when the sky looks dry.
      <br />
      Because someday soon, when the rain comes, you won’t be the one running
      for cover.
      <br />
      You’ll be the one who was ready all along.
    </p>

    <p>Are you operating at the next level?</p>
  </div>
);


  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  return { about, transcript };
};

export default BelievingYouAreNextLevelVideo;
